import { useEffect, useMemo, useState } from 'react';
import { supabase } from '../utils/supabase';
import { pickPositionProfiles, pickPrimaryPositions, type ConsensusRow, type PositionProfile } from '../components/ut/positions';

/**
 * Each player's primary position from peer consensus (player_position_consensus,
 * highest percentage wins). RLS only lets signed-in users read that table, so
 * anonymous visitors get an empty map and cards show XP without a position.
 *
 * Pass `null` to fetch every player (players list, leaderboard). `profiles` holds
 * each player's top two or three positions for describePositions().
 */
export function usePrimaryPositions(playerIds: string[] | null): { positions: Map<string, string>; profiles: Map<string, PositionProfile>; loading: boolean } {
  const [positions, setPositions] = useState<Map<string, string>>(new Map());
  const [profiles, setProfiles] = useState<Map<string, PositionProfile>>(new Map());
  const [loading, setLoading] = useState(true);
  // Stable key so a new array with the same ids doesn't refetch.
  const key = useMemo(() => (playerIds ? [...playerIds].sort().join(',') : '*'), [playerIds]);

  useEffect(() => {
    let cancelled = false;
    if (playerIds && playerIds.length === 0) {
      setPositions(new Map());
      setProfiles(new Map());
      setLoading(false);
      return;
    }
    (async () => {
      setLoading(true);
      try {
        const rows: ConsensusRow[] = [];
        if (playerIds) {
          // Chunk to keep the IN list and the 1000-row cap in check.
          for (let i = 0; i < playerIds.length; i += 100) {
            const { data, error } = await supabase
              .from('player_position_consensus')
              .select('player_id, position, percentage')
              .in('player_id', playerIds.slice(i, i + 100));
            if (error) throw error;
            rows.push(...((data ?? []) as ConsensusRow[]));
          }
        } else {
          for (let from = 0; ; from += 1000) {
            const { data, error } = await supabase
              .from('player_position_consensus')
              .select('player_id, position, percentage')
              .gt('percentage', 0)
              .range(from, from + 999);
            if (error) throw error;
            rows.push(...((data ?? []) as ConsensusRow[]));
            if (!data || data.length < 1000) break;
          }
        }
        if (!cancelled) {
          setPositions(pickPrimaryPositions(rows));
          setProfiles(pickPositionProfiles(rows));
        }
      } catch (err) {
        // Anonymous users hit RLS here; positions are optional decoration.
        if (!cancelled) {
          setPositions(new Map());
          setProfiles(new Map());
        }
        if (import.meta.env.DEV) console.debug('usePrimaryPositions:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return { positions, profiles, loading };
}
